<script>
import OptionListEditor from './OptionListEditor.vue';
import { FIELD_TYPES, UPDATABLE_DEAL_FIELDS } from 'dashboard/constants/stageFormTemplates';

export default {
  name: 'FormFieldItem',
  components: {
    OptionListEditor,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    allFields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:field', 'remove', 'move-up', 'move-down'],
  data() {
    return {
      isExpanded: false,
      fieldTypes: FIELD_TYPES,
      updatableDealFields: UPDATABLE_DEAL_FIELDS,
    };
  },
  computed: {
    localField: {
      get() {
        return this.field;
      },
      set(value) {
        this.$emit('update:field', value);
      },
    },
    needsOptions() {
      return ['select', 'multiselect'].includes(this.localField.type);
    },
    canHaveConditional() {
      // Los campos pueden ser condicionales (excepto si ya son un parent)
      return true;
    },
    availableParentFields() {
      // Solo campos de tipo select/multiselect pueden ser parents
      return this.allFields.filter(
        f => ['select', 'multiselect'].includes(f.type) && f.key !== this.localField.key
      );
    },
    fieldTypeLabel() {
      const type = this.fieldTypes.find(t => t.value === this.localField.type);
      return type ? `${type.icon} ${type.label}` : this.localField.type;
    },
    isSpecialDealField() {
      return this.localField.updates_deal_field && 
             this.localField.updates_deal_field !== '';
    },
    dealFieldConfig() {
      if (!this.isSpecialDealField) return null;
      
      const config = this.updatableDealFields.find(
        f => f.value === this.localField.updates_deal_field
      );
      return config || null;
    },
    shouldDisableTypeAndOptions() {
      return this.dealFieldConfig?.readonly || false;
    },
  },
  watch: {
    'localField.updates_deal_field': {
      handler(newValue) {
        if (newValue && newValue !== '') {
          const config = this.updatableDealFields.find(f => f.value === newValue);
          if (config && config.readonly) {
            // Emitir type + options en un solo update para evitar que la segunda
            // llamada lea el prop desactualizado y revierta el primer cambio
            const patch = { type: config.fieldType };
            if (config.options) {
              patch.options = [...config.options];
            }
            this.localField = { ...this.localField, ...patch };
          }
        }
      },
      immediate: false
    },
    'localField.label': {
      handler(newLabel) {
        // Auto-generar key desde label
        if (newLabel) {
          const autoKey = this.toSnakeCase(newLabel);
          this.updateField('key', autoKey);
        }
      },
      immediate: false
    }
  },
  methods: {
    updateField(key, value) {
      this.localField = { ...this.localField, [key]: value };
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    remove() {
      this.$emit('remove');
    },
    moveUp() {
      this.$emit('move-up');
    },
    moveDown() {
      this.$emit('move-down');
    },
    handleAllowOtherChange(enabled) {
      this.updateField('allowOther', enabled);
      
      // Ya no agregamos "Otro" manualmente a las opciones
      // El frontend (DealMoveForm) lo agregará automáticamente al renderizar
    },
    toSnakeCase(str) {
      if (!str) return '';
      
      return str
        .toString()
        .normalize('NFD') // Descomponer caracteres Unicode (ej: á -> a + acento)
        .replace(/[\u0300-\u036f]/g, '') // Remover acentos
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remover caracteres especiales excepto espacios y guiones
        .replace(/[\s_-]+/g, '_') // Reemplazar espacios, guiones y underscores múltiples por un solo underscore
        .replace(/^_+|_+$/g, ''); // Remover underscores al inicio y final
    },
  },
};
</script>

<template>
  <div class="form-field-item border border-n-weak rounded-lg overflow-hidden bg-white dark:bg-n-solid-2">
    <!-- Header (siempre visible) -->
    <div
      class="flex items-center gap-3 p-3 cursor-pointer hover:bg-n-slate-9/5 transition-colors"
      @click.self="toggleExpanded"
    >
      <!-- Drag handle (futuro) -->
      <div class="text-n-slate-11" @click="toggleExpanded">
        <fluent-icon icon="re-order-dots-vertical" size="16" />
      </div>

      <!-- Info del campo -->
      <div class="flex-1 min-w-0" @click="toggleExpanded">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ localField.label || `Campo ${index + 1}` }}
          </span>
          <span v-if="localField.required" class="text-xs text-n-ruby-10 font-semibold">
            *
          </span>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-11 mt-0.5">
          <span>{{ fieldTypeLabel }}</span>
          <span v-if="localField.key" class="font-mono bg-n-slate-9/10 px-1.5 py-0.5 rounded">
            {{ localField.key }}
          </span>
        </div>
      </div>

      <!-- Botones de acción -->
      <div class="flex items-center gap-1" @click.stop>
        <button
          type="button"
          @click="moveUp"
          class="p-1 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-9/10 rounded transition-colors"
          :title="$t('MOVE_UP')"
        >
          <fluent-icon icon="chevron-up" size="16" />
        </button>
        <button
          type="button"
          @click="moveDown"
          class="p-1 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-9/10 rounded transition-colors"
          :title="$t('MOVE_DOWN')"
        >
          <fluent-icon icon="chevron-down" size="16" />
        </button>
        <button
          type="button"
          @click="remove"
          class="p-1 text-n-ruby-10 hover:text-n-ruby-11 hover:bg-n-ruby-9/10 rounded transition-colors"
          :title="$t('DELETE')"
        >
          <fluent-icon icon="delete" size="16" />
        </button>
      </div>

      <!-- Icono de expand/collapse -->
      <div class="text-n-slate-11" @click="toggleExpanded">
        <fluent-icon :icon="isExpanded ? 'chevron-up' : 'chevron-down'" size="16" />
      </div>
    </div>

    <!-- Contenido expandible -->
    <div v-if="isExpanded" class="border-t border-n-weak p-4 space-y-4 bg-n-slate-9/[0.02]">
      <!-- Label -->
      <div @click.stop>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          Etiqueta <span class="text-n-ruby-10">*</span>
        </label>
        <input
          :value="localField.label"
          type="text"
          placeholder="ej: Motivo de ganancia"
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          @input="updateField('label', $event.target.value)"
          @click.stop
          @keydown.stop
          @keyup.stop
        />
      </div>

      <!-- Tipo de campo -->
      <div @click.stop>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          Tipo de campo <span class="text-n-ruby-10">*</span>
        </label>
        <select
          :value="localField.type"
          :disabled="shouldDisableTypeAndOptions"
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          :class="{ 'opacity-60 cursor-not-allowed': shouldDisableTypeAndOptions }"
          @change="updateField('type', $event.target.value)"
          @click.stop
        >
          <option v-for="type in fieldTypes" :key="type.value" :value="type.value">
            {{ type.icon }} {{ type.label }}
          </option>
        </select>
        <p v-if="shouldDisableTypeAndOptions" class="text-xs text-blue-700 dark:text-blue-300 mt-1">
          🔒 Tipo bloqueado porque actualiza un campo del Deal
        </p>
      </div>

      <!-- Opciones (solo para select/multiselect) -->
      <div v-if="needsOptions && !shouldDisableTypeAndOptions" @click.stop>
        <OptionListEditor
          :model-value="localField.options || []"
          label="Opciones"
          @update:model-value="updateField('options', $event)"
        />
      </div>

      <!-- Info de opciones bloqueadas (cuando actualiza Deal) -->
      <div v-if="needsOptions && shouldDisableTypeAndOptions" class="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-md border border-blue-200 dark:border-blue-800">
        <p class="text-xs text-blue-900 dark:text-blue-100 font-semibold mb-1">
          🔒 Opciones predefinidas del sistema:
        </p>
        <p v-if="dealFieldConfig.options && dealFieldConfig.options.length > 0" class="text-xs text-blue-800 dark:text-blue-200">
          {{ dealFieldConfig.options.join(' • ') }}
        </p>
        <p v-else class="text-xs text-blue-800 dark:text-blue-200 italic">
          Las opciones se cargan dinámicamente desde la API de productos al completar el formulario.
        </p>
        <p class="text-xs text-blue-700 dark:text-blue-300 mt-2">
          Este campo actualiza el Deal, por lo que tipo y opciones no son editables.
        </p>
      </div>

      <!-- Placeholder -->
      <div @click.stop>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          Placeholder
        </label>
        <input
          :value="localField.placeholder"
          type="text"
          placeholder="Texto de ayuda..."
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          @input="updateField('placeholder', $event.target.value)"
          @click.stop
          @keydown.stop
          @keyup.stop
        />
      </div>

      <!-- Texto de ayuda -->
      <div @click.stop>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          Texto de ayuda
        </label>
        <input
          :value="localField.help"
          type="text"
          placeholder="Información adicional para el usuario..."
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          @input="updateField('help', $event.target.value)"
          @click.stop
          @keydown.stop
          @keyup.stop
        />
      </div>

      <!-- Checkbox: Requerido -->
      <div class="flex items-start" @click.stop>
        <input
          :checked="localField.required"
          type="checkbox"
          class="mt-1 mr-2"
          @change="updateField('required', $event.target.checked)"
          @click.stop
        />
        <div>
          <span class="text-sm font-medium text-n-slate-12">Campo obligatorio</span>
          <p class="text-xs text-n-slate-11 mt-0.5">
            El usuario debe completar este campo para enviar el formulario
          </p>
        </div>
      </div>

      <!-- Checkbox: Permitir "Otro" (solo para select/multiselect no bloqueados) -->
      <div v-if="needsOptions && !shouldDisableTypeAndOptions" class="flex items-start" @click.stop>
        <input
          :checked="localField.allowOther"
          type="checkbox"
          class="mt-1 mr-2"
          @change="handleAllowOtherChange($event.target.checked)"
          @click.stop
        />
        <div>
          <span class="text-sm font-medium text-n-slate-12">Permitir opción "Otro"</span>
          <p class="text-xs text-n-slate-11 mt-0.5">
            Agrega automáticamente una opción "Otro" con campo de texto para especificar
          </p>
        </div>
      </div>

      <!-- Actualiza campo del Deal (especial) -->
      <div class="border-t border-n-weak pt-4" @click.stop>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          ⚡ Actualizar campo del Deal
        </label>
        <select
          :value="localField.updates_deal_field || ''"
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          @change="updateField('updates_deal_field', $event.target.value || undefined)"
          @click.stop
        >
          <option v-for="option in updatableDealFields" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        
        <!-- Info box mejorada -->
        <div class="mt-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <div class="flex items-start gap-2">
            <span class="text-lg">💡</span>
            <div class="text-xs text-blue-900 dark:text-blue-100">
              <strong class="block mb-1">Función avanzada:</strong>
              <p class="mb-2">Cuando el usuario completa el formulario, el valor se guarda <strong>tanto</strong> en sus respuestas <strong>como</strong> en el campo correspondiente del Deal.</p>
              <p class="text-blue-700 dark:text-blue-300"><strong>Ejemplo:</strong> En etapa "Ganado", un campo "Monto final" puede actualizar automáticamente el campo monto del deal</p>
            </div>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<style scoped>
/* Estilos adicionales si es necesario */
</style>
